import React, { useState, useCallback } from "react";
import { useMutation } from "react-query";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Typography,
  Avatar,
  Tag,
  Alert,
  message,
  Spin,
  Empty,
  Divider,
} from "antd";
import {
  GiftOutlined,
  UserOutlined,
  SearchOutlined,
  CloseOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { grantCredits, searchUsersForCredits } from "@api/credit/credit";
import debounce from "lodash/debounce";

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Predefined reasons
const REASON_PRESETS = [
  { value: "compensation", label: "Compensation for issue" },
  { value: "promotion", label: "Promotional bonus" },
  { value: "referral", label: "Referral reward" },
  { value: "loyalty", label: "Loyalty bonus" },
  { value: "feedback", label: "Feedback reward" },
  { value: "contest", label: "Contest prize" },
  { value: "beta", label: "Beta tester reward" },
  { value: "custom", label: "Custom reason" },
];

const GrantCreditsModal = ({ visible, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [reasonPreset, setReasonPreset] = useState("custom");

  // Grant credits mutation
  const grantMutation = useMutation(
    async (values) => {
      const res = await grantCredits(
        selectedUsers.map(u => u.userId || u._id),
        values.amount,
        values.reason
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        message.success(`Successfully granted credits to ${selectedUsers.length} user(s)!`);
        handleReset();
        onSuccess?.();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || "Failed to grant credits");
      },
    }
  );

  // Debounced search
  const searchUsers = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const res = await searchUsersForCredits(query, 20);
        const users = res.data?.data || res.data || [];
        // Filter out already selected users
        const filteredUsers = users.filter(
          u => !selectedUsers.some(s => (s.userId || s._id) === (u.userId || u._id))
        );
        setSearchResults(filteredUsers);
      } catch (error) {
        console.error("Search failed:", error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300),
    [selectedUsers]
  );

  const handleSearch = (value) => {
    setSearchValue(value);
    searchUsers(value);
  };

  const handleSelectUser = (user) => {
    if (!selectedUsers.some(u => (u.userId || u._id) === (user.userId || user._id))) {
      setSelectedUsers([...selectedUsers, user]);
    }
    setSearchValue("");
    setSearchResults([]);
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => (u.userId || u._id) !== userId));
  };

  const handleReasonPresetChange = (value) => {
    setReasonPreset(value);
    if (value !== "custom") {
      const preset = REASON_PRESETS.find(p => p.value === value);
      form.setFieldsValue({ reason: preset?.label || "" });
    } else {
      form.setFieldsValue({ reason: "" });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (selectedUsers.length === 0) {
        message.error("Please select at least one user");
        return;
      }

      await grantMutation.mutateAsync(values);
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setSelectedUsers([]);
    setSearchResults([]);
    setSearchValue("");
    setReasonPreset("custom");
  };

  const handleClose = () => {
    handleReset();
    onClose?.();
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "40px",
            height: "40px",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <GiftOutlined style={{ fontSize: "20px", color: "#fff" }} />
          </div>
          <div>
            <Title level={5} style={{ margin: 0 }}>Grant Credits</Title>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Manually award credits to users
            </Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={600}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Button onClick={handleReset} disabled={grantMutation.isLoading}>
            Reset
          </Button>
          <Space>
            <Button onClick={handleClose} disabled={grantMutation.isLoading}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={grantMutation.isLoading}
              icon={<GiftOutlined />}
              style={{ background: "#f59e0b", borderColor: "#f59e0b" }}
            >
              Grant Credits
            </Button>
          </Space>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: "16px" }}>
        {/* User Search */}
        <Form.Item
          label={
            <span>
              Select Users <Text type="secondary">(search by name or email)</Text>
            </span>
          }
        >
          <Input
            placeholder="Search users..."
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            suffix={searching && <Spin size="small" />}
          />

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div style={{
              position: "absolute",
              zIndex: 10,
              width: "100%",
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              marginTop: "4px",
              maxHeight: "200px",
              overflowY: "auto",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}>
              {searchResults.map((user) => (
                <div
                  key={user.userId || user._id}
                  onClick={() => handleSelectUser(user)}
                  style={{
                    padding: "10px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                >
                  <Avatar
                    size={32}
                    src={user.avatar}
                    icon={!user.avatar && <UserOutlined />}
                    style={{ background: user.avatar ? undefined : "#8b5cf6" }}
                  />
                  <div>
                    <Text strong style={{ fontSize: "13px" }}>
                      {user.userName || user.name || "Anonymous"}
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: "11px" }}>
                        {user.email}
                      </Text>
                    </div>
                  </div>
                  {user.totalCredits !== undefined && (
                    <Tag color="green" style={{ marginLeft: "auto" }}>
                      {user.totalCredits} credits
                    </Tag>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div style={{
              marginTop: "12px",
              padding: "12px",
              background: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}>
              <div style={{ marginBottom: "8px", fontSize: "12px", color: "#64748b" }}>
                Selected users ({selectedUsers.length}):
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {selectedUsers.map((user) => (
                  <Tag
                    key={user.userId || user._id}
                    closable
                    onClose={() => handleRemoveUser(user.userId || user._id)}
                    style={{
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <Avatar
                      size={20}
                      src={user.avatar}
                      icon={!user.avatar && <UserOutlined />}
                      style={{ background: user.avatar ? undefined : "#8b5cf6" }}
                    />
                    {user.userName || user.name || user.email}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </Form.Item>

        <Divider style={{ margin: "16px 0" }} />

        {/* Amount */}
        <Form.Item
          name="amount"
          label="Credits Amount"
          rules={[
            { required: true, message: "Please enter the amount" },
            { type: "number", min: 1, message: "Amount must be at least 1" },
            { type: "number", max: 100000, message: "Amount cannot exceed 100,000" },
          ]}
        >
          <InputNumber
            min={1}
            max={100000}
            style={{ width: "100%" }}
            placeholder="Enter credits amount"
            addonBefore={<GiftOutlined />}
            addonAfter="credits"
            size="large"
          />
        </Form.Item>

        {/* Reason Preset */}
        <Form.Item label="Reason Category">
          <Select
            value={reasonPreset}
            onChange={handleReasonPresetChange}
            style={{ width: "100%" }}
          >
            {REASON_PRESETS.map((preset) => (
              <Option key={preset.value} value={preset.value}>
                {preset.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Reason */}
        <Form.Item
          name="reason"
          label="Reason"
          rules={[
            { required: true, message: "Please provide a reason" },
            { min: 5, message: "Reason must be at least 5 characters" },
          ]}
        >
          <TextArea
            rows={3}
            placeholder="Explain why you are granting these credits..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/* Summary */}
        {selectedUsers.length > 0 && form.getFieldValue("amount") > 0 && (
          <Alert
            type="info"
            icon={<CheckCircleOutlined />}
            message="Grant Summary"
            description={
              <div>
                <Text>
                  You are about to grant{" "}
                  <Text strong style={{ color: "#10b981" }}>
                    {(form.getFieldValue("amount") || 0).toLocaleString()} credits
                  </Text>{" "}
                  to{" "}
                  <Text strong>{selectedUsers.length} user(s)</Text>.
                </Text>
                <div style={{ marginTop: "8px" }}>
                  <Text type="secondary">
                    Total credits to be awarded:{" "}
                    <Text strong style={{ color: "#10b981" }}>
                      {((form.getFieldValue("amount") || 0) * selectedUsers.length).toLocaleString()}
                    </Text>
                  </Text>
                </div>
              </div>
            }
            style={{ marginTop: "16px" }}
          />
        )}
      </Form>
    </Modal>
  );
};

export default GrantCreditsModal;
